import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { NAVBAR_HEIGHT } from "../../components/Navbar/NavDesktop";
import "../../components/Scorm/scormApi";
import { usePortal } from "../../context/PortalsContext";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import useScreenSize from "../../hooks/useScreenSize";
import {
  getUserAssignment,
  getHomeAssignments,
  logInteraction,
  submitAcknowledgement,
} from "../../services/api/apiClient";
import {
  AssignmentMenuItem,
  ProgressStatus,
  UserAssignmentNote,
  type HomeAssignment as HomeAssignmentType,
  UserAssignmentsMode,
} from "../../services/api/types/general/homeAssignment";
import { PackageType } from "../../services/api/types/assignments/assignment";
import { gray } from "../../theme/themePrimitives";
import { CourseContent } from "./components/CourseContent";
import { CourseProgress } from "./components/CourseProgress";
import { Notes } from "./components/Notes";
import { AcknowledgementPanel } from "./components/AcknowledgementPanel";
import {
  setAssignmentMode,
  setCompletionPercentage,
  setInteractionSession,
  setStopRedirect,
  resetScormData,
  setReturnPath,
} from "../../components/Scorm/scormApi";
import { usePageMetadata } from "../../context/PageMetadataContext";
import { useAssignmentLayout } from "../../context/AssignmentLayoutContext";
import { extractErrorMessage } from "../../utils/errorUtils";

const HomeAssignment = () => {
  const { assignmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [assignmentUrl, setAssignmentUrl] = useState("");
  const [packageType, setPackageType] = useState<string | undefined>();
  const [pageByPage, setPageByPage] = useState(false);
  const [acknowledgementEnabled, setAcknowledgementEnabled] = useState(true);
  const [progressStatus, setProgressStatus] = useState<
    ProgressStatus | undefined
  >();
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);
  const [pdfReachedEnd, setPdfReachedEnd] = useState(false);
  const [pdfInitialPage, setPdfInitialPage] = useState(1);
  const [videoReachedEnd, setVideoReachedEnd] = useState(false);
  const [videoInitialTime, setVideoInitialTime] = useState(0);
  const [isVideoMandatory, setIsVideoMandatory] = useState(false);
  const [pdfInteractionSession, setPdfInteractionSession] = useState("");
  const [ackSubmission, setAckSubmission] = useState<{
    decision: boolean;
    comment: string;
  } | null>(null);
  const [notes, setNotes] = useState<UserAssignmentNote[]>([]);
  const [assignmentMenuItems, setAssignmentMenuItems] = useState<
    AssignmentMenuItem[]
  >([]);
  const { isMobile, isTablet } = useScreenSize();
  const [mobileTab, setMobileTab] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [maxChapter, setMaxChapter] = useState<string>("0");
  const [loading, setLoading] = useState(false);
  const [parentAssignment, setParentAssignment] =
    useState<HomeAssignmentType | null>(null);
  const { showToast } = useToast();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const maxChapterRef = useRef(maxChapter);
  const { auth } = useAuth();
  const { portals } = usePortal();
  const locationState = location.state as {
    mode?: UserAssignmentsMode;
    parentAssignment?: HomeAssignmentType;
    parentPath?: string;
    parentBreadcrumbs?: Array<{ label: string; path?: string }>;
  } | null;
  const assignmentMode = useMemo<UserAssignmentsMode | undefined>(() => {
    if (!assignmentId) return undefined;

    return locationState?.mode;
  }, [assignmentId, locationState?.mode]);

  useEffect(() => {
    maxChapterRef.current = maxChapter;
  }, [maxChapter]);

  // Scroll to top when entering this page (iframe load can leave page scrolled down)
  const scrollToTop = useCallback(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    scrollToTop();
    const id = setTimeout(scrollToTop, 150);
    return () => clearTimeout(id);
  }, [assignmentId, scrollToTop]);

  // Keep SCORM API completion % in sync so Terminate/LMSFinish can redirect only when course is finished (>= 100%).
  useEffect(() => {
    if (!assignmentMenuItems.length) return;
    let maxPage = 0;
    assignmentMenuItems.forEach((chapter) => {
      maxPage = Math.max(maxPage, parseInt(chapter.PageNo, 10));
      chapter.subchapters?.forEach((sub) => {
        maxPage = Math.max(maxPage, parseInt(sub.PageNo, 10));
      });
    });
    if (maxPage === 0) return;
    const pct = Math.round((parseInt(maxChapter, 10) / maxPage) * 100);
    setCompletionPercentage(String(pct));
  }, [maxChapter, assignmentMenuItems]);

  useEffect(() => {
    if (!assignmentMenuItems.length) {
      setMobileTab(0);
      setActiveTab(0);
    }
  }, [assignmentMenuItems.length]);

  const handleMobileTabChange = (event: any, newValue: number) => {
    setMobileTab(newValue);
  };
  const handleTabChange = (event: any, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAcknowledge = useCallback(
    async (decision: boolean, comment: string) => {
      if (
        !portals.selectedPortal?.portalUid ||
        !auth.user?.UserPortalUid ||
        !assignmentId ||
        assignmentMode !== UserAssignmentsMode.ENROLLED
      ) {
        return;
      }
      await submitAcknowledgement(
        portals.selectedPortal.portalUid,
        auth.user.UserPortalUid,
        assignmentId,
        {
          ackResponse: decision,
          comment,
        },
      );
      setAckSubmission({ decision, comment });
    },
    [assignmentId, assignmentMode, auth.user, portals.selectedPortal],
  );

  // Automatic, silent progress tracking for page-by-page PDFs via logInteraction
  // cmi.location/lesson_location behavior: every page turn saves the current
  // page as the resume position, no explicit "bookmark" action needed.
  const handlePdfPageChange = useCallback(
    (page: number) => {
      if (
        portals.selectedPortal?.portalUid &&
        auth.user?.UserPortalUid &&
        assignmentId &&
        assignmentMode === UserAssignmentsMode.ENROLLED
      ) {
        const rawPercentage = pdfNumPages
          ? Math.round((page / pdfNumPages) * 100)
          : undefined;
        const completionPercentage =
          rawPercentage === undefined ? undefined : String(rawPercentage);
        logInteraction(
          portals.selectedPortal.portalUid,
          auth.user.UserPortalUid,
          assignmentId,
          {
            interactionSession: pdfInteractionSession,
            interactionData: JSON.stringify({ page, numPages: pdfNumPages }),
            completionPercentage,
            isTerminate: false,
          },
        ).catch(() => {});
      }
    },
    [
      assignmentId,
      assignmentMode,
      auth.user,
      portals.selectedPortal,
      pdfNumPages,
      pdfInteractionSession,
    ],
  );

  // Automatic, silent completion tracking for scroll-mode PDFs: page-by-page
  // mode already logs progress via handlePdfPageChange on every page turn,
  // so only log here when the continuous-scroll viewer reaches its end.
  const handlePdfReachedEnd = useCallback(() => {
    setPdfReachedEnd(true);
    if (
      pageByPage ||
      !portals.selectedPortal?.portalUid ||
      !auth.user?.UserPortalUid ||
      !assignmentId ||
      assignmentMode !== UserAssignmentsMode.ENROLLED
    ) {
      return;
    }
    logInteraction(
      portals.selectedPortal.portalUid,
      auth.user.UserPortalUid,
      assignmentId,
      {
        interactionSession: pdfInteractionSession,
        interactionData: JSON.stringify({
          page: pdfNumPages,
          numPages: pdfNumPages,
        }),
        completionPercentage: "100",
        isTerminate: false,
      },
    ).catch(() => {});
  }, [
    pageByPage,
    assignmentId,
    assignmentMode,
    auth.user,
    portals.selectedPortal,
    pdfNumPages,
    pdfInteractionSession,
  ]);

  // Automatic, silent progress tracking for video playback via logInteraction,
  // mirroring handlePdfPageChange — called on a throttled timeupdate from the
  // video player so the resume position is saved without an explicit action.
  const handleVideoProgress = useCallback(
    (currentTime: number, duration: number) => {
      if (
        !portals.selectedPortal?.portalUid ||
        !auth.user?.UserPortalUid ||
        !assignmentId ||
        assignmentMode !== UserAssignmentsMode.ENROLLED
      ) {
        return;
      }
      const completionPercentage = duration
        ? String(Math.round((currentTime / duration) * 100))
        : undefined;
      logInteraction(
        portals.selectedPortal.portalUid,
        auth.user.UserPortalUid,
        assignmentId,
        {
          interactionSession: pdfInteractionSession,
          interactionData: JSON.stringify({ currentTime, duration }),
          completionPercentage,
          isTerminate: false,
        },
      ).catch(() => {});
    },
    [
      assignmentId,
      assignmentMode,
      auth.user,
      portals.selectedPortal,
      pdfInteractionSession,
    ],
  );

  const handleVideoReachedEnd = useCallback(() => {
    setVideoReachedEnd(true);
    if (
      !portals.selectedPortal?.portalUid ||
      !auth.user?.UserPortalUid ||
      !assignmentId ||
      assignmentMode !== UserAssignmentsMode.ENROLLED
    ) {
      return;
    }
    logInteraction(
      portals.selectedPortal.portalUid,
      auth.user.UserPortalUid,
      assignmentId,
      {
        interactionSession: pdfInteractionSession,
        interactionData: JSON.stringify({ currentTime: null, duration: null }),
        completionPercentage: "100",
        isTerminate: false,
      },
    ).catch(() => {});
  }, [
    assignmentId,
    assignmentMode,
    auth.user,
    portals.selectedPortal,
    pdfInteractionSession,
  ]);

  // Get parent assignment from location state or fetch it
  useEffect(() => {
    const getParentAssignment = async () => {
      if (!auth.user || !portals.selectedPortal || !assignmentId) return;

      // First, try to get parent from location state
      const locationState = location.state as {
        parentAssignment?: HomeAssignmentType;
        parentPath?: string;
      } | null;
      if (locationState?.parentAssignment) {
        setParentAssignment(locationState.parentAssignment);
        return;
      }

      // If not in state, try to find parent by searching through assignments
      try {
        // Fetch root level assignments
        const rootAssignments = await getHomeAssignments(
          auth.user.UserPortalUid,
          portals.selectedPortal.portalUid,
          { groupLevel: 0 },
        );

        // Check if assignment is in root level
        const foundInRoot = rootAssignments.find(
          (a) => a.assignmentId === assignmentId,
        );
        if (foundInRoot) {
          // No parent, it's a root level assignment
          setParentAssignment(null);
          return;
        }

        // Search through nested levels
        for (const rootAssignment of rootAssignments) {
          if (!rootAssignment.isDirectAssignment) {
            const nestedAssignments = await getHomeAssignments(
              auth.user.UserPortalUid,
              portals.selectedPortal.portalUid,
              { groupLevel: 1, portalGroupId: rootAssignment.assignmentId },
            );

            const found = nestedAssignments.find(
              (a) => a.assignmentId === assignmentId,
            );
            if (found) {
              setParentAssignment(rootAssignment);
              return;
            }
          }
        }
      } catch (error) {
        console.error("Failed to find parent assignment:", error);
      }
    };

    getParentAssignment();
  }, [auth.user, portals.selectedPortal, assignmentId, location.state]);

  useEffect(() => {
    const fetchAssignment = async () => {
      // Reset per-assignment PDF viewer state so switching assignments
      // doesn't carry over a stale "reached end" / page count / bookmark.
      setPdfNumPages(null);
      setPdfReachedEnd(false);
      setPdfInitialPage(1);
      setVideoReachedEnd(false);
      setVideoInitialTime(0);
      setIsVideoMandatory(false);
      setAcknowledgementEnabled(true);
      setPackageType(undefined);
      setPdfInteractionSession("");
      setAckSubmission(null);

      if (auth.user && portals.selectedPortal && assignmentId) {
        try {
          setLoading(true);
          const result = await getUserAssignment(
            portals.selectedPortal.portalUid,
            auth.user?.UserPortalUid,
            assignmentId,
            assignmentMode ? { mode: assignmentMode } : undefined,
          );
          if (result.Mode) {
            setAssignmentMode(result.Mode);
          }
          if (result.InteractionSession) {
            setInteractionSession(result.InteractionSession);
            setPdfInteractionSession(result.InteractionSession);
          }
          setAcknowledgementEnabled(result.Acknowledgement ?? true);
          setProgressStatus(result.ProgressStatus);
          setAckSubmission(
            result.SubmittedAt
              ? {
                  decision: `${result.AckResponse}`.toLowerCase() === "true",
                  comment: result.Comment ?? "",
                }
              : null,
          );
          setPackageType(result.PackageType);
          setIsVideoMandatory(result.Mandatory ?? false);
          setPageByPage(result.PageByPage ?? false);
          // Notes/MenuItems, Url and Title are independent of one another —
          // a PDF assignment has no chapters (empty/absent MenuItems) but
          // still needs its Url and Title set, so these must not be gated
          // behind a Notes+MenuItems check.
          setNotes(result.Notes ?? []);
          setAssignmentMenuItems(result.MenuItems ?? []);
          setAssignmentUrl(`${result.Url}`);
          if (result?.Title) {
            setAssignmentTitle(result.Title);
          }
          if (result?.Bookmark) {
            if (window.API_1484_11) {
              sendScormData("cmi.suspend_data", result.Bookmark);
            }
            if (window.API) {
              sendScormDataLMS("cmi.suspend_data", result.Bookmark);
            }

            const match = result.Bookmark.match(/progress:(\d+)/);
            if (match) {
              const progressValue = parseInt(match[1], 10);
              if (window.API_1484_11) {
                sendScormData("cmi.location", `${progressValue},on,off,en`);
              }
              if (window.API) {
                sendScormDataLMS(
                  "cmi.core.lesson_location",
                  `${progressValue},on,off,en`,
                );
              }
              setMaxChapter(progressValue.toString());
              setPdfInitialPage(progressValue > 0 ? progressValue : 1);
              setVideoInitialTime(progressValue > 0 ? progressValue : 0);
            }
          }
        } catch (error: any) {
          console.log("Error:", error);
          showToast(
            extractErrorMessage(error, "Failed to fetch user assignment"),
            "error",
          );
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAssignment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, portals.selectedPortal, assignmentId, assignmentMode]);

  const sendScormData = useCallback(
    (key: any, value: any) => {
      if (window.API_1484_11) {
        window.API_1484_11.SetValue(key, value);
        window.API_1484_11.Commit();
        window.API_1484_11.GetValue(key);
        window.API_1484_11.Commit();

        // Update maxChapter when location is set (SCORM 2004)
        if (key === "cmi.location") {
          const progressValue = value.split(",")[0];
          const currentMaxChapter = parseInt(maxChapterRef.current, 10);
          const newProgressValue = parseInt(progressValue, 10);
          if (newProgressValue > currentMaxChapter) {
            setMaxChapter(progressValue);
          }
        }

        if (iframeRef.current) {
          iframeRef.current.src = `/${assignmentUrl}?t=${new Date().getTime()}`;
        }
      } else {
        console.error("SCORM API not found on window object");
      }
    },
    [assignmentUrl],
  );

  const sendScormDataLMS = useCallback(
    (key: any, value: any) => {
      if (window.API) {
        window.API.LMSSetValue(key, value);
        window.API.LMSCommit();
        window.API.LMSGetValue(key);
        window.API.LMSCommit();

        // Update maxChapter when lesson_location is set (SCORM 1.2)
        if (key === "cmi.core.lesson_location") {
          const progressValue = value.split(",")[0];
          const currentMaxChapter = parseInt(maxChapterRef.current, 10);
          const newProgressValue = parseInt(progressValue, 10);
          if (newProgressValue > currentMaxChapter) {
            setMaxChapter(progressValue);
          }
        }

        if (iframeRef.current) {
          iframeRef.current.src = `/${assignmentUrl}?t=${new Date().getTime()}`;
        }
      } else {
        console.error("SCORM API not found on window object");
      }
    },
    [assignmentUrl],
  );

  const hasCourseContent = assignmentMenuItems.length > 0;
  const isPdfAssignment = packageType
    ? packageType === PackageType.PdfUpload
    : null;
  const isVideoAssignment = packageType
    ? packageType === PackageType.VideoUpload
    : null;
  const showAcknowledgement =
    !!(isPdfAssignment || isVideoAssignment) && acknowledgementEnabled;
  // Acknowledgement may have been toggled on after the learner already
  // completed the assignment (e.g. no acknowledgement was required at the
  // time) — reuse the existing "unlocked" gate so answering stays locked.
  const assignmentAlreadyCompleted =
    progressStatus === ProgressStatus.COMPLETED;
  const pdfAckUnlocked = pdfReachedEnd && !assignmentAlreadyCompleted;
  const videoAckUnlocked = videoReachedEnd && !assignmentAlreadyCompleted;
  const ackUnlocked = isVideoAssignment ? videoAckUnlocked : pdfAckUnlocked;
  const unlockHint = isVideoAssignment
    ? "Watch the full video to unlock this section."
    : `View all ${pdfNumPages ?? "the"} pages to unlock this section.`;
  const notesMobileTabIndex = hasCourseContent || showAcknowledgement ? 1 : 0;
  const notesDesktopTabIndex = hasCourseContent || showAcknowledgement ? 1 : 0;

  const assignmentLayout = useAssignmentLayout();
  const setSidebarContentRef = useRef(assignmentLayout?.setSidebarContent);
  setSidebarContentRef.current = assignmentLayout?.setSidebarContent;
  const setPackageTypeRef = useRef(assignmentLayout?.setPackageType);
  setPackageTypeRef.current = assignmentLayout?.setPackageType;

  // Memoize sidebar content so we don't trigger state updates with a new element every render
  const assignmentSidebarContent = useMemo(
    () => (
      <>
        <Box sx={{ borderBottom: `1px solid ${gray[300]}`, width: "100%" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ width: "100%", background: "#fff" }}
          >
            {hasCourseContent && (
              <Tab
                label="Course content"
                sx={{ flex: 1, whiteSpace: "nowrap" }}
              />
            )}
            {showAcknowledgement && (
              <Tab label="Acknowledgement" sx={{ flex: 1 }} />
            )}
            {assignmentMode !== UserAssignmentsMode.PREVIEW && (
              <Tab label="Notes" sx={{ flex: 1 }} />
            )}
          </Tabs>
        </Box>
        {hasCourseContent && activeTab === 0 && (
          <CourseProgress
            assignmentMenuItems={assignmentMenuItems}
            maxChapter={maxChapter}
            sendScormData={sendScormData}
            sendScormDataLMS={sendScormDataLMS}
          />
        )}
        {showAcknowledgement && activeTab === 0 && (
          <AcknowledgementPanel
            unlocked={ackUnlocked}
            alreadyCompleted={assignmentAlreadyCompleted}
            unlockHint={unlockHint}
            onSubmit={handleAcknowledge}
            submittedAck={ackSubmission}
          />
        )}
        {(!hasCourseContent && !showAcknowledgement
          ? true
          : activeTab === notesDesktopTabIndex) &&
          assignmentMode === UserAssignmentsMode.ENROLLED && (
            <Notes
              assignmentId={assignmentId ?? ""}
              notes={notes}
              setNotes={setNotes}
            />
          )}
      </>
    ),
    [
      activeTab,
      hasCourseContent,
      showAcknowledgement,
      unlockHint,
      handleAcknowledge,
      ackSubmission,
      notesDesktopTabIndex,
      assignmentMenuItems,
      maxChapter,
      sendScormData,
      sendScormDataLMS,
      notes,
      assignmentId,
      assignmentMode,
      ackUnlocked,
      assignmentAlreadyCompleted,
    ],
  );

  // When PageLayout uses assignment grid (desktop), feed right sidebar so it starts at navbar.
  // Use ref for setSidebarContent to avoid depending on context object (which changes every render -> infinite loop).
  useEffect(() => {
    const setSidebar = setSidebarContentRef.current;
    if (!setSidebar || !assignmentId) return;
    setSidebar(assignmentSidebarContent);
    return () => setSidebar(null);
  }, [assignmentId, assignmentSidebarContent]);

  // Let PageLayout know this assignment's package type so it can style accordingly.
  useEffect(() => {
    setPackageTypeRef.current?.(packageType as PackageType | undefined);
  }, [packageType]);

  const parentPath =
    locationState?.parentPath ||
    (parentAssignment
      ? `/home/assignments/${parentAssignment.assignmentId}`
      : "/home");

  useEffect(() => {
    setReturnPath(parentPath);
  }, [parentPath]);

  const parentCrumbsFromState = locationState?.parentBreadcrumbs;

  usePageMetadata(
    {
      title: "",
      previewText:
        assignmentMode === UserAssignmentsMode.PREVIEW ? "Preview" : undefined,
      breadcrumbs: [
        ...(parentCrumbsFromState?.length
          ? parentCrumbsFromState.map((c, idx) => ({
              label: c.label,
              path:
                idx === parentCrumbsFromState.length - 1 ? undefined : c.path,
              onClick: c.path
                ? () => {
                    resetScormData();
                    setStopRedirect(true);
                    navigate(c.path!);
                  }
                : undefined,
            }))
          : [
              {
                label: "Home",
                path: "/home",
                onClick: () => {
                  resetScormData();
                  setStopRedirect(true);
                  navigate("/home");
                },
              },
              ...(parentAssignment
                ? [
                    {
                      label: parentAssignment.title,
                      path: parentPath,
                      onClick: () => {
                        resetScormData();
                        setStopRedirect(true);
                        navigate(parentPath);
                      },
                    },
                  ]
                : []),
            ]),
        {
          label: assignmentTitle,
        },
      ],
    },
    [parentAssignment, assignmentTitle, parentPath, navigate, assignmentMode],
  );

  return (
    <Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : (
        <>
          <Box
            sx={
              isMobile || isTablet
                ? {
                    display: "flex",
                    flexDirection: "column",
                  }
                : assignmentLayout
                  ? {
                      width: "100%",
                      minWidth: 0,
                      minHeight: isTablet
                        ? "calc(100vh - 104px)"
                        : `calc(100vh - ${NAVBAR_HEIGHT})`,
                      position: "relative",
                    }
                  : {
                      width: "100%",
                      minWidth: 0,
                      display: "grid",
                      gridTemplateColumns: "1fr 24%",
                      minHeight: `calc(100vh - ${NAVBAR_HEIGHT})`,
                      position: "relative",
                    }
            }
          >
            {assignmentId && (
              <>
                {isMobile || isTablet ? (
                  <>
                    <Box
                      sx={{
                        borderBottom: `1px solid ${gray[300]}`,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Tabs
                        value={mobileTab}
                        onChange={handleMobileTabChange}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{
                          minHeight: "unset",
                        }}
                      >
                        {hasCourseContent && (
                          <Tab
                            label="Course content"
                            sx={{
                              width: "42vw",
                              whiteSpace: "nowrap",
                            }}
                          />
                        )}
                        {showAcknowledgement && (
                          <Tab
                            label="Acknowledgement"
                            sx={{
                              width: "42vw",
                            }}
                          />
                        )}
                        {assignmentMode !== UserAssignmentsMode.PREVIEW && (
                          <Tab
                            label="Notes"
                            sx={{
                              width:
                                hasCourseContent || showAcknowledgement
                                  ? "42vw"
                                  : "100%",
                            }}
                          />
                        )}
                      </Tabs>
                    </Box>
                    <Box>
                      {hasCourseContent && (
                        <Box
                          sx={{ display: mobileTab === 0 ? undefined : "none" }}
                        >
                          <Box sx={{ marginTop: "1rem", px: 1 }}>
                            <CourseContent
                              assignmentUrl={assignmentUrl}
                              assignmentId={assignmentId}
                              iframeRef={iframeRef}
                              setMaxChapter={setMaxChapter}
                              maxChapter={maxChapterRef}
                            />
                          </Box>
                          <CourseProgress
                            assignmentMenuItems={assignmentMenuItems}
                            sendScormData={sendScormData}
                            sendScormDataLMS={sendScormDataLMS}
                            maxChapter={maxChapter}
                          />
                        </Box>
                      )}
                      {isPdfAssignment && (
                        <Box
                          sx={{ display: mobileTab === 0 ? undefined : "none" }}
                        >
                          <Box sx={{ marginTop: "1rem", px: 1 }}>
                            <CourseContent
                              assignmentUrl={assignmentUrl}
                              assignmentId={assignmentId}
                              assignmentTitle={assignmentTitle}
                              iframeRef={iframeRef}
                              setMaxChapter={setMaxChapter}
                              maxChapter={maxChapterRef}
                              isPdf
                              pageByPage={pageByPage}
                              pdfInitialPage={pdfInitialPage}
                              onPdfDocumentLoad={setPdfNumPages}
                              onPdfReachedEnd={handlePdfReachedEnd}
                              onPdfPageChange={handlePdfPageChange}
                              requiresAcknowledgement={showAcknowledgement}
                            />
                          </Box>
                          {showAcknowledgement && (
                            <AcknowledgementPanel
                              unlocked={ackUnlocked}
                              alreadyCompleted={assignmentAlreadyCompleted}
                              unlockHint={unlockHint}
                              onSubmit={handleAcknowledge}
                              submittedAck={ackSubmission}
                            />
                          )}
                        </Box>
                      )}
                      {isVideoAssignment && (
                        <Box
                          sx={{ display: mobileTab === 0 ? undefined : "none" }}
                        >
                          <Box sx={{ marginTop: "1rem", px: 1 }}>
                            <CourseContent
                              assignmentUrl={assignmentUrl}
                              assignmentId={assignmentId}
                              assignmentTitle={assignmentTitle}
                              iframeRef={iframeRef}
                              setMaxChapter={setMaxChapter}
                              maxChapter={maxChapterRef}
                              isVideo
                              videoInitialTime={videoInitialTime}
                              onVideoProgress={handleVideoProgress}
                              onVideoReachedEnd={handleVideoReachedEnd}
                              isVideoMandatory={isVideoMandatory}
                              videoMandatoryAlreadySatisfied={
                                assignmentAlreadyCompleted
                              }
                            />
                          </Box>
                          {showAcknowledgement && (
                            <AcknowledgementPanel
                              unlocked={ackUnlocked}
                              alreadyCompleted={assignmentAlreadyCompleted}
                              unlockHint={unlockHint}
                              onSubmit={handleAcknowledge}
                              submittedAck={ackSubmission}
                            />
                          )}
                        </Box>
                      )}
                      {assignmentMode === UserAssignmentsMode.ENROLLED && (
                        <Box
                          sx={{
                            display:
                              (!hasCourseContent && !showAcknowledgement) ||
                              mobileTab === notesMobileTabIndex
                                ? undefined
                                : "none",
                          }}
                        >
                          <Notes
                            assignmentId={assignmentId}
                            notes={notes}
                            setNotes={setNotes}
                          />
                        </Box>
                      )}
                    </Box>
                  </>
                ) : assignmentLayout ? (
                  <Box
                    sx={{
                      minWidth: 0,
                      minHeight: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <CourseContent
                      assignmentId={assignmentId}
                      assignmentUrl={assignmentUrl}
                      iframeRef={iframeRef}
                      setMaxChapter={setMaxChapter}
                      maxChapter={maxChapterRef}
                      assignmentTitle={assignmentTitle}
                      isPdf={!!isPdfAssignment}
                      pageByPage={pageByPage}
                      pdfInitialPage={pdfInitialPage}
                      onPdfDocumentLoad={setPdfNumPages}
                      onPdfReachedEnd={handlePdfReachedEnd}
                      onPdfPageChange={handlePdfPageChange}
                      isVideo={!!isVideoAssignment}
                      videoInitialTime={videoInitialTime}
                      onVideoProgress={handleVideoProgress}
                      onVideoReachedEnd={handleVideoReachedEnd}
                      isVideoMandatory={isVideoMandatory}
                      videoMandatoryAlreadySatisfied={
                        assignmentAlreadyCompleted
                      }
                      requiresAcknowledgement={showAcknowledgement}
                    />
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        minWidth: 0,
                        minHeight: "100%",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <CourseContent
                        assignmentId={assignmentId}
                        assignmentUrl={assignmentUrl}
                        assignmentTitle={assignmentTitle}
                        iframeRef={iframeRef}
                        setMaxChapter={setMaxChapter}
                        maxChapter={maxChapterRef}
                        isPdf={!!isPdfAssignment}
                        pageByPage={pageByPage}
                        pdfInitialPage={pdfInitialPage}
                        onPdfDocumentLoad={setPdfNumPages}
                        onPdfReachedEnd={handlePdfReachedEnd}
                        onPdfPageChange={handlePdfPageChange}
                        isVideo={!!isVideoAssignment}
                        videoInitialTime={videoInitialTime}
                        onVideoProgress={handleVideoProgress}
                        onVideoReachedEnd={handleVideoReachedEnd}
                        isVideoMandatory={isVideoMandatory}
                        videoMandatoryAlreadySatisfied={
                          assignmentAlreadyCompleted
                        }
                        requiresAcknowledgement={showAcknowledgement}
                      />
                    </Box>
                    <Box
                      sx={{
                        minWidth: 0,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        borderLeft: "1px solid rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <Box
                        sx={{
                          borderBottom: `1px solid ${gray[300]}`,
                          width: "100%",
                        }}
                      >
                        <Tabs
                          value={activeTab}
                          onChange={handleTabChange}
                          textColor="primary"
                          indicatorColor="primary"
                          variant="fullWidth"
                          sx={{
                            width: "100%",
                          }}
                        >
                          {hasCourseContent && (
                            <Tab
                              label="Course content"
                              sx={{
                                flex: 1,
                                whiteSpace: "nowrap",
                              }}
                            />
                          )}
                          {showAcknowledgement && (
                            <Tab
                              label="Acknowledgement"
                              sx={{
                                flex: 1,
                              }}
                            />
                          )}
                          {assignmentMode !== UserAssignmentsMode.PREVIEW && (
                            <Tab
                              label="Notes"
                              sx={{
                                flex: 1,
                              }}
                            />
                          )}
                        </Tabs>
                      </Box>
                      {hasCourseContent && activeTab === 0 && (
                        <CourseProgress
                          assignmentMenuItems={assignmentMenuItems}
                          maxChapter={maxChapter}
                          sendScormData={sendScormData}
                          sendScormDataLMS={sendScormDataLMS}
                        />
                      )}
                      {showAcknowledgement && activeTab === 0 && (
                        <AcknowledgementPanel
                          unlocked={ackUnlocked}
                          alreadyCompleted={assignmentAlreadyCompleted}
                          unlockHint={unlockHint}
                          onSubmit={handleAcknowledge}
                          submittedAck={ackSubmission}
                        />
                      )}
                      {(!hasCourseContent && !showAcknowledgement
                        ? true
                        : activeTab === notesDesktopTabIndex) &&
                        assignmentMode === UserAssignmentsMode.ENROLLED && (
                          <Notes
                            assignmentId={assignmentId}
                            notes={notes}
                            setNotes={setNotes}
                          />
                        )}
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default HomeAssignment;
